/**
 * @fileoverview vue コンポーネント（クラス）へ標準的に mixin しておきたい内容。
 *               これは Vue Plugin であり、 Vue.use() することで利用可能となる。
 */

import _ from 'underscore'
import $ from 'jquery'
import uuidv4 from 'uuid/v4'
import { utils } from './utils'


/**
 * ユーティリティメソッド群全体または任意のものを取り出したオブジェクトを返す。
 * @param {string[]|{[key:string]: string}} [props]    取り出すメソッドの名称リスト。
 *   - [例1] メソッド名を羅列： [ 'method1', 'method2', ... ]
 *   - [例2] 読み替え連想配列： { myMethod1: 'method1' , myMethod2: 'method2', ... }
 *   - [例3] 無指定。この場合はすべてのメソッドが取り出される
 * @return {{[key:string]: Function}}
 * @inner
 */
function mapUtilMethods (props) {
  if (_.isArray(props)) return _.pick(utils, props)
  if (_.isObject(props)) return _.mapObject(prop => (utils[prop] || _.noop))
  return utils
}


const mixins = {
  data () {
    return {
      scrollLockApplicant: null,
    }
  },

  computed: {
  },

  watch: {
  },

  mounted () {
  },

  beforeDestroy () {
  },

  methods: {
    /**
     * この Vue Component の最外殻の要素ノード自身、あるいは内部の要素ノードに、キーボードフォーカスがあたっているかどうか。
     * NOTE: もとが focusable ではない要素ノードへのマウスクリックでフォーカスを失わないためには、
     *       適宜 tabfocus 属性をつけてキーボードフォーカスが維持されるようにする必要がある。
     * @return {boolean}
     */
    hasFocus () {
      return this.$hasFocus()
    },

    /**
     * mixin 先の component に上書きされた hasFocus() が本来の処理を必要とするとき、これを呼び出す
     * @private
     * */
    $hasFocus () {
      const $base = $(this.$el)
      const focused = document.activeElement
      return $base.is(focused) || !!$base.has(focused).length
    },

    /**
     * コンテンツのスクロールロックを要請する
     * @return {Vue}
     * @private
     */
    setScrollLock () {
      if (!this.scrollLockApplicant) {
        this.$store.dispatch('uiState/appendScrollLockApplicant', this.scrollLockApplicant = {
          uuid: uuidv4(),
          name: this.$options.name || 'Unknown',
        })
      }
      return this
    },

    /**
     * コンテンツのスクロールロック要請を取下
     * @return {Vue}
     * @private
     */
    unsetScrollLock () {
      if (this.scrollLockApplicant) {
        this.$store.dispatch('uiState/removeScrollLockApplicant', this.scrollLockApplicant)
        this.$reset({ targets: [ 'scrollLockApplicant' ] })
      }
      return this
    },

    /**
     * 背景画像参照用の CSS 値を生成。
     * @param {string} [url]    背景画像。URL 文字列。
     * @return {string}  CSS 値のテキスト
     * @private
     */
    url (url) {
      return !url ? '' : `url(${url})`
    },

    /**
     * Vue Component ローカルに保持しているステートをまっさらな状態へ戻す
     * @param {Object}   payload              引数オブジェクト。メンバーは以下のもの
     * @param {string[]} [payload.targets]    リセット対象のプロパティ名を並べた配列。無指定時は当該 Store まるごとのリセット
     * @param {string[]} [payload.excepts]    リセット対象から除外するプロパティ名を並べた配列。
     * @private
     */
    reset (payload) {
      return this.$reset(payload)
    },

    /**
     * mixin 先の component に上書きされた reset() が本来の処理を必要とするとき、これを呼び出す
     * @param {Object}   payload              引数オブジェクト。メンバーは以下のもの
     * @param {string[]} [payload.targets]    リセット対象のプロパティ名を並べた配列。無指定時は当該 Store まるごとのリセット
     * @param {string[]} [payload.excepts]    リセット対象から除外するプロパティ名を並べた配列。
     * @private
     */
    $reset ({ targets, excepts }) {
      // "targets" プロパティ名を書き間違えていると大惨事になりそうだから安全策
      const noArgs = !arguments.length

      targets = noArgs ? [] : _.toArray(targets)
      excepts = noArgs ? [] : _.toArray(excepts)

      let origin = this.$options.data()
          origin = targets.length ? _.pick(origin, targets) : noArgs ? origin : {}  // リセット対象のプロパティのみ残す
          origin = _.omit(origin, excepts)                                          // リセット対象から除外するプロパティを除外

      Object.assign(this, origin)
      return this
    },
  },

  filters: {
    // utils (utils.js) のメンバーメソッドのいくつかを
    // Vue Component の filter メソッドとして使えるようにする
    ...mapUtilMethods([
      'count',
      'reverse',
      'numeral',
      'datetime',
      'pick',
      'omit',
      'omitBefore',
      'omitAfter',
      'lefthand',
      'righthand',
    ]),

    /**
     * @see http://underscorejs.org/#pluck
     */
    pluck: _.pluck,

    /**
     * @see http://underscorejs.org/#first
     */
    first: _.first,

    /**
     * @see http://underscorejs.org/#last
     */
    last: _.last,

    /**
     * @see http://underscorejs.org/#initial
     */
    initial: _.initial,

    /**
     * @see http://underscorejs.org/#rest
     */
    rest: _.rest,


    // これらのほか、 vue2-filters 由来の filter も用意されている
    // https://github.com/freearhey/vue2-filters
  },
}


export default {
  install: Vue => Vue.mixin(mixins)
}

